import type { ChangeEvent, FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import SelectOutlined from 'src/assets/svg/select_outlined.svg?react';
import SelectOutlinedUnchecked from 'src/assets/svg/select_outlined_unchecked.svg?react';
import {
  PERMISSION_GROUP_NAME,
  PermissionGroup,
} from 'src/constants/modulePermission';
import { SECTIONS_WITH_READ_ONLY_OPTION } from 'src/constants/section';
import { usePrevious } from 'src/hooks';
import type { ModulePermission } from 'src/types/modulePermission';
import type { ModuleOrPlanActionOrgUnit } from 'src/types/orgUnit';
import type { UserDetail } from 'src/types/userModule';
import { changeItem, removeItem } from 'src/helper/helper';
import { useModuleItems } from 'src/components/app/context/ModuleItemsContext';
import useUserDetail from 'src/components/userAdmin/UserAdminDetailContext';
import { CopyItemSelectorContent } from 'src/components/userAdmin/UserDetailPage/components/CopyItemSelector';
import OrgUnitSelector from 'src/components/userAdmin/UserDetailPage/components/OrgUnitSelector';

import { styles } from './index.styles';

export const PERMISSION_CARD_ADMIN_TEST_ID = 'permission-card-admin';
export const PERMISSION_CARD_ORG_UNIT_TEST_ID = 'permission-card-org-unit';
export const PERMISSION_CARD_HAS_ACCESS_TEST_ID = 'permission-card-has-access';

export const PERMISSION_CARD_HEADER_TEST_ID = 'permission-card-header';

export interface PermissionCardProps {
  modulePermission: ModulePermission;
}

const PermissionCard: FC<PermissionCardProps> = ({ modulePermission }) => {
  const { has_access, read_only, module, org_units, permission_group } =
    modulePermission;
  const { t } = useTranslation('admin');
  const [permission, setPermission] = useState(modulePermission);
  const { setHasAccess, setPermissionRole, setOrgUnits } = useModuleItems();
  const prevPermissions = usePrevious(permission);
  const { user, handlePermissionChange, intersection, userModules } =
    useUserDetail();
  const hasReadOnlyField = SECTIONS_WITH_READ_ONLY_OPTION.includes(
    module.module_type,
  );

  const isActive = useMemo(
    () => userModules.some((item) => item.module.id === module.id),
    [userModules],
  );

  const { orgUnits, disallowSelectionFor } = useMemo(
    () => ({
      orgUnits: [...org_units, undefined],
      disallowSelectionFor: org_units.map(({ id }) => id),
    }),
    [org_units],
  );

  /**
   * Handle permission_group or has_access change.
   */
  const handleChange = (event: ChangeEvent<any> | SelectChangeEvent<any>) => {
    let {
      // eslint-disable-next-line prefer-const
      target: { name: fieldName, value },
      // eslint-disable-next-line prefer-const
      target,
    } = event;

    if (target.type === 'checkbox') {
      value = !JSON.parse(value); // Be cautious of html form values being stored as strings.
    }

    setPermission({ ...permission, [fieldName]: value });
  };

  /**
   * Handle selecting an OrgUnit.
   */
  const handleSelectOrgUnit = (
    index: number,
    orgUnit: ModuleOrPlanActionOrgUnit,
  ) => {
    setPermission({
      ...permission,
      org_units: changeItem(permission.org_units, index, orgUnit),
    });
  };

  const handleRemoveOrgUnit = (index: number) => {
    setPermission({
      ...permission,
      org_units: removeItem(permission.org_units, index),
    });
  };

  // Allow controlling state through property.
  useEffect(() => {
    setPermission(modulePermission);
  }, [modulePermission]);

  // Fire callback property on permission change.
  useEffect(() => {
    // Make sure of the following before firing the callback:
    // - A callback function is provided
    // - The permission change did not originate from initializing the permission state
    // - The internal modulePermission actually differs from the passed modulePermission (avoid infinite loop)
    if (
      !!handlePermissionChange &&
      !!prevPermissions &&
      permission !== modulePermission
    ) {
      handlePermissionChange(permission);
    }
  }, [permission]);

  const handleCopy = () => {
    setHasAccess(!!has_access);
    setPermissionRole(permission_group);
    setOrgUnits(org_units as ModuleOrPlanActionOrgUnit[]);
  };

  const getPermissionOptions = (currentUser: UserDetail) => {
    if (currentUser.role?.canBeAdmin) {
      return Object.entries(PERMISSION_GROUP_NAME);
    }
    return Object.entries(PERMISSION_GROUP_NAME).filter(
      ([permissionGroupId]) =>
        permissionGroupId !== String(PermissionGroup.ADMIN),
    );
  };

  return (
    <Box
      data-test-id={`permission-card-${module.name}${module.id || ''}`}
      sx={styles.permissionCard(isActive)}
    >
      <Box
        sx={styles.permissionCardHeader(isActive)}
        data-test-id={PERMISSION_CARD_HEADER_TEST_ID}
      >
        <Typography
          variant="h4"
          sx={{
            fontSize: 16,
          }}
        >
          {module.name}
        </Typography>
        <CopyItemSelectorContent
          headline={t('admin:Detail.CopyModule.copyModule')}
          accessibleModules={intersection}
          moduleName={module.name}
          handleCopy={handleCopy}
        />
      </Box>
      {/* Access to Tool */}
      <Box data-test-id="access-to-tool-holder">
        <Stack direction="row" sx={styles.permissionCardRow}>
          <FormControlLabel
            sx={styles.checkboxes}
            control={
              <Checkbox
                size="medium"
                icon={
                  <SelectOutlinedUnchecked width="1.5rem" height="1.5rem" />
                }
                checkedIcon={<SelectOutlined width="1.5rem" height="1.5rem" />}
                onChange={handleChange}
                checked={has_access}
                value={has_access}
                name="has_access"
                data-test-id={PERMISSION_CARD_HAS_ACCESS_TEST_ID}
                disableRipple
              />
            }
            label={t('Permission.AccessToTool')}
          />
        </Stack>

        {/* Read-only Permissions - currently this is only in place for HRQS Ticket Overview and Follow-Up Actions. */}
        {hasReadOnlyField && (
          <Stack direction="row" sx={{ gap: 1, ...styles.permissionCardRow }}>
            <Box sx={{ width: '70%' }}>
              <FormControlLabel
                sx={styles.checkboxes}
                control={
                  <Checkbox
                    size="medium"
                    icon={
                      <SelectOutlinedUnchecked width="1.5rem" height="1.5rem" />
                    }
                    checkedIcon={
                      <SelectOutlined width="1.5rem" height="1.5rem" />
                    }
                    onChange={handleChange}
                    checked={read_only}
                    value={read_only}
                    name="read_only"
                    disableRipple
                  />
                }
                label={t('Permission.ReadOnlyAccessToTool')}
              />
            </Box>
          </Stack>
        )}

        {/* Select User Role */}
        <Stack direction="row" sx={styles.permissionCardRow}>
          <Box>
            <Typography sx={{ fontWeight: 600, width: '120px' }}>
              {t('Permission.UserRole')}
            </Typography>
          </Box>
          <Box sx={styles.inputWrapper}>
            <Select
              fullWidth
              sx={styles.dropdown}
              name="permission_group"
              onChange={handleChange}
              value={permission_group}
              data-test-id={PERMISSION_CARD_ADMIN_TEST_ID}
            >
              <MenuItem>{t('Permission.ChooseUserRole')}</MenuItem>

              {getPermissionOptions(user).map(
                ([permissionGroupId, permissionGroupName]) => (
                  <MenuItem
                    selected
                    key={permissionGroupId}
                    value={permissionGroupId}
                  >
                    {permissionGroupName}
                  </MenuItem>
                ),
              )}
            </Select>
          </Box>
        </Stack>
        <Divider sx={styles.horizontalDivider(isActive)} />
      </Box>
      {orgUnits.map((orgUnit, index) => (
        <Box data-test-id={PERMISSION_CARD_ORG_UNIT_TEST_ID} key={index}>
          <OrgUnitSelector
            disallowSelectionFor={disallowSelectionFor}
            index={index}
            module={module}
            onRemove={handleRemoveOrgUnit}
            onSelect={handleSelectOrgUnit}
            orgUnit={orgUnit}
          />
        </Box>
      ))}
    </Box>
  );
};
export default PermissionCard;
